import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    VIEW, YES, ACTIVE, IDLE, PENDING, COMPLIANCE, REVIEW,
} from "const"
import { hasProjectAccess, StateType } from "client/utils"
import {
    PRM_CC_REVIEW,
    PRM_CC_RESULTS_PDF,
    StoreType,
    CcReviewDocumentProps,
    CcReviewComplianceProps,
    CcReviewReviewProps,

    LoadCcApprovalLevels,
    LoadCcResponses,
    LoadCcReviewReviewTypes,
    LoadAllProjectGroupUsers,
    SetCcReviewHref,

    setCcApprovalLevelList,
    setCcModuleApprovalLevelList,
    setCcResponseList,
    setCcModuleResponseList,
    setCcReviewReviewTypeList,
    setCcReviewReviewTypeIds,
    setProjectGroupUserList,

    setCcReviewDocumentBaseRoute,
    setCcReviewDocumentActiveTab,

    moduleIds,
} from "client/store"
import {
    projectsRoute,
    projectBaseRoute,
    projectCcReviewRoute,
} from "client/parts/routes"
import {
    Switch,
    Route,
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuButton,
    Tab,
    Loader,
    Button,

    LangReportWrapper,
    LangReportSwitcher,
} from "client/components"

import ExportToPDF from "client/pages/cc/exportToPDF"

import AccessDenied from "client/parts/accessDenied"
import FileList from "./fileList"
import ComplianceList from "./compliance/list"
import ReviewList from "./review/list"
import { ComplianceStatistics, ReviewStatistics } from "./statistics"


const CcReview = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const moduleList = useSelector((state: StoreType) => state.module.moduleList)
    const dispatch = useDispatch()

    const { ccModuleAccess } = packageSettings
    const moduleAccess = ccModuleAccess === YES && moduleList[moduleIds.cc].status === ACTIVE
    const viewAccess = hasProjectAccess(PRM_CC_REVIEW, VIEW)

    const baseRouteMask = `${projectBaseRoute}${projectCcReviewRoute}`
    const baseRoute = `${projectsRoute}/${VIEW}/${project.id}${projectCcReviewRoute}`

    useEffect(() => {
        dispatch(setCcReviewDocumentBaseRoute(baseRoute))
    }, [])

    if (!moduleAccess || !viewAccess) return <AccessDenied />
    return (
        <Switch>
            <Route
                // view compliance answer
                path={`${baseRouteMask}/:tab/file/:fileId/version/:versionId/set/:setId/revision/:revisionId/item/:complianceItemId`}
                component={CcReviewWrapper}
            />
            <Route
                // edit review item
                path={`${baseRouteMask}/:tab/file/:fileId/version/:versionId/editItem/:reviewItemId/revision/:revisionId`}
                component={CcReviewWrapper}
            />
            <Route
                // view review answer
                path={`${baseRouteMask}/:tab/file/:fileId/version/:versionId/item/:reviewItemId/revision/:revisionId`}
                component={CcReviewWrapper}
            />
            <Route
                path={`${baseRouteMask}/:tab/file/:fileId/version/:versionId`}
                component={CcReviewWrapper}
            />
            <Route
                path={`${baseRouteMask}/:tab/file/:fileId`}
                component={CcReviewWrapper}
            />
            <Route
                path={`${baseRouteMask}/:tab`}
                component={CcReviewWrapper}
            />
            <Route
                path={baseRouteMask}
                component={CcReviewWrapper}
            />
        </Switch>
    )
}
export default CcReview


const menu = [COMPLIANCE, REVIEW]

interface CcReviewProps extends
    CcReviewDocumentProps,
    CcReviewComplianceProps,
    CcReviewReviewProps
    {}

const CcReviewWrapper = ({
    tab,
    fileId,
    versionId,
    setId,
    revisionId,
    complianceItemId,
    reviewItemId,
}: CcReviewProps) => {
    const baseRoute = useSelector((state: StoreType) => state.ccReviewDocument.baseRoute)
    const activeTab = useSelector((state: StoreType) => state.ccReviewDocument.activeTab)
    const activeFile = useSelector((state: StoreType) => state.ccReviewDocument.activeFile)
    const activeVersion = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)

    const documentsLoadState = useSelector((state: StoreType) => state.ccReviewDocument.loadState)
    const complianceSetLoadState = useSelector((state: StoreType) => state.ccReviewCompliance.setLoadState)
    const complianceItemLoadState = useSelector((state: StoreType) => state.ccReviewCompliance.itemLoadState)
    const reviewLoadState = useSelector((state: StoreType) => state.ccReviewReview.loadState)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [showExport, setShowExport] = useState(false)

    useEffect(() => {
        dispatch(setCcReviewDocumentActiveTab(menu.includes(tab) ? tab : COMPLIANCE))
    }, [])

    useEffect(() => {
        setLoadState(PENDING)
        Promise.all([
            // review approval levels
            LoadCcApprovalLevels(userData.accountId, project.id, 2)
            .then((res) => {
                dispatch(setCcApprovalLevelList(res.levelList))
                dispatch(setCcModuleApprovalLevelList({ 2: res.levelIds }))
            }),
            // formal approval levels
            LoadCcApprovalLevels(userData.accountId, project.id, 0)
            .then((res) => {
                dispatch(setCcApprovalLevelList(res.levelList))
                dispatch(setCcModuleApprovalLevelList({ 0: res.levelIds }))
            }),
            // review (compliance) responses
            LoadCcResponses(userData.accountId, project.id, 2)
            .then((res) => {
                dispatch(setCcResponseList(res.responseList))
                dispatch(setCcModuleResponseList({ 2: res.responseIds }))
            }),
            // review (review) responses
            LoadCcResponses(userData.accountId, project.id, 3)
            .then((res) => {
                dispatch(setCcResponseList(res.responseList))
                dispatch(setCcModuleResponseList({ 3: res.responseIds }))
            }),
            // responsible responses
            LoadCcResponses(userData.accountId, project.id, 0)
            .then((res) => {
                dispatch(setCcResponseList(res.responseList))
                dispatch(setCcModuleResponseList({ 0: res.responseIds }))
            }),
            // review types
            LoadCcReviewReviewTypes(userData.accountId, project.id)
            .then((res) => {
                dispatch(setCcReviewReviewTypeList(res.typeList))
                dispatch(setCcReviewReviewTypeIds(res.typeIds))
            }),
            LoadAllProjectGroupUsers(userData.accountId, project.id)
            .then((res) => {
                dispatch(setProjectGroupUserList(res))
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [])

    const handleMenuClick = (targetTab: string) => {
        dispatch(setCcReviewDocumentActiveTab(targetTab))
        SetCcReviewHref(baseRoute, targetTab, activeFile && activeFile.id, activeVersion && activeVersion.id)
    }

    const viewPdfAccess = hasProjectAccess(PRM_CC_RESULTS_PDF, VIEW)

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.review}</PageTitle>
                {
                    viewPdfAccess && (
                        <LangReportWrapper>
                            <LangReportSwitcher />
                            <Button
                                variant="success"
                                disabled={loadState === PENDING}
                                onClick={() => setShowExport(true)}
                            >
                                {lang.export}
                            </Button>
                        </LangReportWrapper>
                    )
                }
            </PageHeader>
            <PageContent>
                <PageMenu>
                    <PageMenuButton
                        isActive={activeTab === COMPLIANCE}
                        onClick={() => handleMenuClick(COMPLIANCE)}
                    >
                        {lang.compliance}
                    </PageMenuButton>
                    <PageMenuButton
                        isActive={activeTab === REVIEW}
                        onClick={() => handleMenuClick(REVIEW)}
                    >
                        {lang.review}
                    </PageMenuButton>
                </PageMenu>

                {
                    (
                        loadState === PENDING
                        || documentsLoadState === PENDING
                        || complianceSetLoadState === PENDING
                        || complianceItemLoadState === PENDING
                        || reviewLoadState === PENDING
                    ) && <Loader />
                }

                <FileList
                    fileId={fileId}
                    versionId={versionId}
                />

                <Tab show={activeTab === COMPLIANCE}>
                    <ComplianceStatistics />
                    <ComplianceList
                        setId={setId}
                        revisionId={revisionId}
                        complianceItemId={complianceItemId}
                    />
                </Tab>
                <Tab show={activeTab === REVIEW}>
                    <ReviewStatistics />
                    <ReviewList
                        reviewItemId={reviewItemId}
                        revisionId={revisionId}
                    />
                </Tab>

                <ExportToPDF
                    show={showExport}
                    defaultReport={REVIEW}
                    onClose={() => setShowExport(false)}
                />
            </PageContent>
        </Container>
    )
}
